import { Instance, Instances } from '@react-three/drei';
import { useDotAnimation } from './hooks/use-dot-animation';
import { ExperienceColor } from '@/lib/schemas/experience-colors';
import { ShapeRef, VisualizationType } from './types';
import { type Mesh } from 'three';
import { useRef } from 'react';
import { experienceColorsToDots } from './utils/experience-colors-to-dots';
import { ShapeVennDiagram } from './shape-venn-diagram';
import { ShapeSpinningRings } from './shape-spinning-rings';
import { ShapeRingSorted, ShapeRingUnsorted } from './shape-ring';
import { ShapeBlobs } from './shape-blobs';
import { useThree } from '@react-three/fiber';
import { ScreenSizer } from '../ScreenSizer';
interface PillarsCanvasContentProps {
  visualizationType: VisualizationType;
  experienceColors: ExperienceColor[];
}

export function PillarsCanvasContent({
  visualizationType,
  experienceColors,
}: PillarsCanvasContentProps) {
  const shapeRef = useRef<ShapeRef>(null);
  const dotsRef = useRef<ReturnType<typeof experienceColorsToDots> | null>(
    null
  );

  const getDots = () => {
    if (dotsRef.current !== null) return dotsRef.current;

    dotsRef.current = experienceColorsToDots(experienceColors);

    return dotsRef.current;
  };

  const { isInitialPositioningDone } = useDotAnimation({
    shapeRef,
    dots: getDots(),
  });

  const ShapeComponent = {
    unsortedRing: ShapeRingUnsorted,
    blobs: ShapeBlobs,
    sortedRing: ShapeRingSorted,
    vennDiagram: ShapeVennDiagram,
    spinningRings: ShapeSpinningRings,
  }[visualizationType];

  const { width, height } = useThree((x) => x.size);

  const minimumSize = Math.min(width, height);

  return (
    <>
      <ScreenSizer>
        <group
          scale={[minimumSize, minimumSize, minimumSize]}
          visible={isInitialPositioningDone}
        >
          <Instances limit={experienceColors.length}>
            <sphereGeometry args={[0.5, 16, 32]} />

            <meshBasicMaterial transparent opacity={0.8} />

            {getDots().map((dot, dotIndex) => (
              <group key={dotIndex}>
                <Instance
                  color={dot.hexColorId}
                  ref={(mesh) => {
                    if (mesh) {
                      dot.mesh = mesh as Mesh;
                    }
                  }}
                />
              </group>
            ))}
          </Instances>
        </group>
      </ScreenSizer>
      <ShapeComponent dotCount={experienceColors.length} ref={shapeRef} />
    </>
  );
}
